import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { apiPost, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { maybeClaimReferral } from "@/lib/referral";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/");
      return;
    }

    (async () => {
      try {
        // Tell the backend which client this login is from, for the single-session device lock.
        const isPWA =
          window.matchMedia?.("(display-mode: standalone)")?.matches ||
          window.navigator?.standalone === true;
        const me = await apiPost("/auth/session", null, {
          "X-Session-ID": sessionId,
          "X-Client-Type": isPWA ? "PWA" : "WEB",
        });
        setToken(me?.session_token); // store bearer token so the PWA isn't reliant on the blocked cookie
        setUser(me);
        await maybeClaimReferral(me?.is_new_user);
        if (me?.is_new_user) {
          const n = me.gift_credits || 4;
          toast.success(`🎁 Welcome! ${n} Free Premium Credits have been added to your account.`, {
            duration: 6000,
          });
        }
        window.history.replaceState(null, "", "/app");
        // Land on the freshest build for BOTH web and the installed app: clear the
        // cached shell then HARD-navigate (an SPA navigate would keep a stale bundle).
        try {
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
        } catch (e) { console.error("cache clear on login failed (ignored):", e); }
        window.location.replace("/app");
      } catch (e) {
        setError(
          e?.message ||
            "We couldn't sign you in just now. The sign-in service may be busy — please try again."
        );
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6" data-testid="auth-callback">
      {error ? (
        <div className="max-w-md w-full border-2 border-black p-6 text-center" data-testid="auth-error">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-red-600">Sign-in didn’t go through</p>
          <p className="mt-3 text-sm text-black" data-testid="auth-error-message">{error}</p>
          <button
            className="mt-5 w-full border-2 border-black bg-[#FFD400] px-4 py-3 text-sm font-bold uppercase tracking-[0.08em] transition-colors hover:bg-black hover:text-[#FFD400]"
            onClick={() => (window.location.href = "/")}
            data-testid="auth-error-home-btn"
          >
            Try signing in again
          </button>
        </div>
      ) : (
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-black">Signing you in…</p>
      )}
    </div>
  );
}
